import { createSlice } from '@reduxjs/toolkit';
import { processAppointments } from 'utils';
import { createAsyncAction } from '../action/index';

const getAppointments = createAsyncAction('getAppointments', 'getAppointments');
const getAppointment = createAsyncAction('getAppointment', 'getAppointment');
const bookAppointment = createAsyncAction('bookAppointment', 'bookAppointment');
const bookZeroFeeTeleConsult = createAsyncAction('bookZeroFeeTeleConsult', 'bookZeroFeeTeleConsult');
const getTeleConsultSession = createAsyncAction('getTeleConsultSession', 'getTeleConsultSession');
const getLatestTeleConsultPayment = createAsyncAction('getLatestTeleConsultPayment', 'getLatestTeleConsultPayment');
const initiateTeleConsultPayment = createAsyncAction('initiateTeleConsultPayment', 'initiateTeleConsultPayment');
const getTeleConsultPricing = createAsyncAction('getTeleConsultPricing', 'getTeleConsultPricing');
const getProfileToken = createAsyncAction('getProfileToken', 'getProfileToken');
const getReceipt = createAsyncAction('getReceipt', 'getReceipt');
const deleteAppointment = createAsyncAction('deleteAppointment', 'deleteAppointment');
const diagnosticOrder = createAsyncAction('diagnosticOrder', 'diagnosticOrder');

interface InitialState {
  appointments: any[],
  appointment: any,
  appointmentErrors: any,
  appointmentsByYear: any;
  years: any;
  token: any;
  receipt: any;
  bookedAppointment: any;
  bookAppointmentErrors: any;
  consultSession: any;
  consultPayment: any;
  initiatedConsultPayment: any;
  teleConsultPricing: any;
  hasMoreAppointment: boolean,
  isFetchingAppointments: boolean,
  isCancelingAppointment: boolean;
  cancelingAppointmentError: string;
  isAppointmentCanceled: boolean;
  isFetchingAppointment: boolean,
  isBookingAppointment: boolean,
  isZeroFreeTeleAppointmentBooked: boolean,
  isBookingZeroFreeTeleAppointment: boolean,
  isFetchingConsultSession: boolean,
  isFetchingConsultPayment: boolean,
  isInitiatingConsultPayment: boolean,
  isFetchingTeleConsultPricing: boolean,
  isFetchingReceipt: boolean,
  closingBookingModal: boolean,
}

const initialState = {
  appointments: [],
  appointment: {},
  appointmentErrors: {},
  appointmentsByYear: {},
  years: [],
  token: {},
  receipt: {},
  bookedAppointment: {},
  bookAppointmentErrors: {},
  consultSession: {},
  consultPayment: {},
  initiatedConsultPayment: {},
  teleConsultPricing: {},
  cancelingAppointmentError: '',
  hasMoreAppointment: true,
  isFetchingAppointments: true,
  isCancelingAppointment: false,
  isAppointmentCanceled: false,
  isFetchingAppointment: false,
  isBookingAppointment: false,
  isZeroFreeTeleAppointmentBooked: false,
  isBookingZeroFreeTeleAppointment: false,
  isFetchingConsultSession: false,
  isFetchingConsultPayment: false,
  isInitiatingConsultPayment: false,
  isFetchingTeleConsultPricing: false,
  isFetchingReceipt: false,
  closingBookingModal: false,
} as InitialState;

export const counterSlice = createSlice({
  name: 'appointment',
  initialState,
  reducers: {
    updateAppointmentState: (state, action) => Object.assign(state, action.payload),
  },
  extraReducers: {
    [getAppointments.pending as any]: (state, { meta }) => {
      state.isFetchingAppointments = true;
      if (meta.arg.params.page === 1) {
        state.appointments = [];
        state.appointmentsByYear = {};
        state.years = [];
      }
    },
    [getAppointments.fulfilled as any]: (state, { payload, meta }) => {
      const appointments = payload?.user?.appointments || [];
      const processData = processAppointments([...state.appointments, ...appointments]);
      state.appointments = processData.appointments;
      state.appointmentsByYear = processData.appointmentsByYear;
      state.years = processData.years;
      state.hasMoreAppointment = meta.arg.params.offset === appointments.length;
      state.isFetchingAppointments = false;
    },
    [getAppointments.rejected as any]: (state, { error }) => {
      if (error.name !== 'AbortError')
        state.isFetchingAppointments = false;
    },
    [getAppointment.pending as any]: (state) => {
      state.isFetchingAppointment = true;
      state.appointment = {};
    },
    [getAppointment.fulfilled as any]: (state, { payload }) => {
      state.isFetchingAppointment = false;
      state.appointment = payload.appointment;
    },
    [getAppointment.rejected as any]: (state, { error, payload }) => {
      if (error.name !== 'AbortError') {
        state.appointmentErrors = payload;
        state.isFetchingAppointment = false;
      }
    },
    [bookAppointment.pending as any]: (state) => {
      state.isBookingAppointment = true;
      state.bookedAppointment = {};
    },
    [bookAppointment.fulfilled as any]: (state, { payload }) => {
      state.isBookingAppointment = false;
      state.bookedAppointment = payload || {};
    },
    [bookAppointment.rejected as any]: (state, { payload }) => {
      state.isBookingAppointment = false;
      state.bookedAppointment = {};
      state.bookAppointmentErrors = payload || {};
    },
    [bookZeroFeeTeleConsult.pending as any]: (state) => {
      state.isBookingZeroFreeTeleAppointment = true;
      state.isZeroFreeTeleAppointmentBooked = false;
    },
    [bookZeroFeeTeleConsult.fulfilled as any]: (state) => {
      state.isBookingZeroFreeTeleAppointment = false;
      state.isZeroFreeTeleAppointmentBooked = true;
    },
    [bookZeroFeeTeleConsult.rejected as any]: (state) => {
      state.isBookingZeroFreeTeleAppointment = false;
      state.isZeroFreeTeleAppointmentBooked = false;
    },
    [getTeleConsultSession.pending as any]: (state) => {
      state.isFetchingConsultSession = true;
    },
    [getTeleConsultSession.fulfilled as any]: (state, { payload }) => {
      state.isFetchingConsultSession = false;
      state.consultSession = payload;
    },
    [getTeleConsultSession.rejected as any]: (state, { error }) => {
      if (error.name !== 'AbortError')
        state.isFetchingConsultSession = false;
    },
    [getLatestTeleConsultPayment.pending as any]: (state) => {
      state.isFetchingConsultPayment = true;
    },
    [getLatestTeleConsultPayment.fulfilled as any]: (state, { payload }) => {
      state.isFetchingConsultPayment = false;
      state.consultPayment = payload;
    },
    [getLatestTeleConsultPayment.rejected as any]: (state, { error }) => {
      if (error.name !== 'AbortError')
        state.isFetchingConsultPayment = false;
    },
    [initiateTeleConsultPayment.pending as any]: (state) => {
      state.isInitiatingConsultPayment = true;
    },
    [initiateTeleConsultPayment.fulfilled as any]: (state, { payload }) => {
      state.isInitiatingConsultPayment = false;
      state.initiatedConsultPayment = payload;
    },
    [initiateTeleConsultPayment.rejected as any]: (state) => {
      state.isInitiatingConsultPayment = false;
    },
    [getTeleConsultPricing.pending as any]: (state) => {
      state.teleConsultPricing = {};
      state.isFetchingTeleConsultPricing = true;
    },
    [getTeleConsultPricing.fulfilled as any]: (state, { payload }) => {
      state.teleConsultPricing = payload;
      state.isFetchingTeleConsultPricing = false;
    },
    [getTeleConsultPricing.rejected as any]: (state, { error }) => {
      if (error.name !== 'AbortError') {
        state.teleConsultPricing = {};
        state.isFetchingTeleConsultPricing = false;
      }
    },
    [deleteAppointment.pending as any]: (state) => {
      state.isCancelingAppointment = true;
      state.isAppointmentCanceled = false;
      state.cancelingAppointmentError = '';
    },
    [deleteAppointment.fulfilled as any]: (state) => {
      state.isCancelingAppointment = false;
      state.isAppointmentCanceled = true;
    },
    [deleteAppointment.rejected as any]: (state, { payload }) => {
      state.isCancelingAppointment = false;
      state.isAppointmentCanceled = false;
      state.cancelingAppointmentError = payload?.error || '';
    },
    [getProfileToken.pending as any]: (state) => {
      state.token = {};
    },
    [getProfileToken.fulfilled as any]: (state, { payload }) => {
      state.token = payload.token;
    },
    [getReceipt.pending as any]: (state) => {
      state.receipt = {};
      state.isFetchingReceipt = true;
    },
    [getReceipt.fulfilled as any]: (state, { payload }) => {
      state.receipt = payload;
      state.isFetchingReceipt = false;
    },
    [getReceipt.rejected as any]: (state, { error }) => {
      if (error.name !== 'AbortError') {
        state.isFetchingReceipt = false;
      }
    },
  },
});

// Action creators are generated for each case reducer function
// export const { increment, decrement, incrementByAmount, } = counterSlice.actions
const { updateAppointmentState } = counterSlice.actions;
export {
  getAppointments,
  getAppointment,
  bookAppointment,
  bookZeroFeeTeleConsult,
  getTeleConsultSession,
  getLatestTeleConsultPayment,
  initiateTeleConsultPayment,
  getTeleConsultPricing,
  deleteAppointment,
  getProfileToken,
  getReceipt,
  diagnosticOrder,
  updateAppointmentState,
};
export default counterSlice.reducer;